import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';

function ResetPassword() {
  const [formData, setFormData] = useState({
    email: '',
    resetCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });
  const navigate = useNavigate();

  // Get email from session storage on component mount
  useEffect(() => {
    const resetEmail = sessionStorage.getItem('resetEmail');
    if (!resetEmail) {
      toast.error('Please start the password reset process from the forgot password page');
      navigate('/forgot-password');
      return;
    }
    setFormData(prev => ({ ...prev, email: resetEmail }));
  }, [navigate]);

  const checkPasswordStrength = (password) => {
    let score = 0;
    const feedback = [];

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('At least 8 characters');
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include lowercase letters');
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include uppercase letters');
    }

    if (/[0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include numbers');
    }

    if (password.length >= 12) {
      score += 1;
    }

    return { score, feedback };
  };

  const getStrengthColor = (score) => {
    if (score <= 1) return '#ef4444';
    if (score <= 2) return '#f59e0b';
    if (score <= 3) return '#3b82f6';
    return '#10b981';
  };

  const getStrengthText = (score) => {
    if (score <= 1) return 'Weak';
    if (score <= 2) return 'Fair';
    if (score <= 3) return 'Good';
    return 'Strong';
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, newPassword });
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.resetCode.length !== 6) {
      toast.error('Reset code must be 6 digits');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(formData.email, formData.resetCode, formData.newPassword);
      
      // Clear session storage
      sessionStorage.removeItem('resetEmail');
      
      toast.success('Password reset successfully! You can now login with your new password.');
      navigate('/login');
    } catch (error) {
      toast.error(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container-split">
      {/* Left Side - Gradient with Reset Theme */}
      <div className="login-gradient-side">
        <div className="login-gradient-content">
          <div className="login-brand-icon">
            <img 
              src="/assets/logo.png" 
              alt="OUK Logo" 
              className="gradient-logo"
            />
          </div>
          <div style={{ textAlign: 'center', color: 'white', marginTop: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px' }}>
              🔑 Create New Password
            </h2>
            <p style={{ fontSize: '16px', opacity: 0.9, lineHeight: '1.6' }}>
              Enter the 6-digit code from your email and create a new secure password 
              for your account.
            </p>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '12px', 
              padding: '20px', 
              marginTop: '30px',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Security Tips:</h3>
              <ul style={{ textAlign: 'left', fontSize: '14px', opacity: 0.9 }}>
                <li style={{ marginBottom: '8px' }}>Use a mix of letters, numbers</li>
                <li style={{ marginBottom: '8px' }}>Make it at least 8 characters long</li>
                <li style={{ marginBottom: '8px' }}>Avoid common words or patterns</li>
                <li>Don't reuse old passwords</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="login-gradient-shapes">
          <div className="gradient-shape shape-1"></div>
          <div className="gradient-shape shape-2"></div>
          <div className="gradient-shape shape-3"></div>
          <div className="gradient-shape shape-4"></div>
        </div>
      </div>

      {/* Right Side - Reset Password Form */}
      <div className="login-form-side">
        <div className="login-form-container">
          <div className="login-form-header">
            <div className="login-form-icon">
              <img 
                src="/assets/logo.png" 
                alt="OUK Logo" 
                className="login-logo"
              />
            </div>
            <h2 className="login-form-title">Reset Password</h2>
            <p style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', marginTop: '8px' }}>
              Enter the reset code from your email and create a new password
            </p>
            {formData.email && (
              <p style={{ color: '#037b90', fontSize: '12px', textAlign: 'center', marginTop: '4px', fontWeight: '600' }}>
                Reset code sent to: {formData.email}
              </p>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label">Reset Code</label>
              <input
                type="text"
                className="login-input"
                placeholder="Enter 6-digit reset code"
                value={formData.resetCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setFormData({ ...formData, resetCode: value });
                }}
                maxLength="6"
                style={{ 
                  textAlign: 'center', 
                  fontSize: '18px', 
                  letterSpacing: '4px',
                  fontFamily: 'monospace'
                }}
                required
              />
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', textAlign: 'center' }}>
                Check your email for the 6-digit code
              </p>
            </div>
            
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="password-input-container">
                <input
                  type={showPasswords.new ? "text" : "password"}
                  className="login-input"
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <button 
                  type="button" 
                  className="password-toggle"
                  onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                >
                  <span>{showPasswords.new ? '🙈' : '👁'}</span>
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                      Password Strength
                    </span>
                    <span style={{ 
                      fontSize: '12px', 
                      fontWeight: '700', 
                      color: getStrengthColor(passwordStrength.score) 
                    }}>
                      {getStrengthText(passwordStrength.score)}
                    </span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '6px', 
                    backgroundColor: '#f1f5f9', 
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${(passwordStrength.score / 5) * 100}%`, 
                      height: '100%', 
                      backgroundColor: getStrengthColor(passwordStrength.score),
                      transition: 'all 0.3s ease',
                      borderRadius: '3px'
                    }}></div>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      {passwordStrength.feedback.map((tip, index) => (
                        <div key={index} style={{ 
                          fontSize: '11px', 
                          color: '#f59e0b', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          marginBottom: '2px'
                        }}>
                          <span>⚠</span>
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <div className="password-input-container">
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  className={`login-input ${formData.confirmPassword && formData.newPassword !== formData.confirmPassword ? 'error' : formData.confirmPassword && formData.newPassword === formData.confirmPassword ? 'success' : ''}`}
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                />
                <button 
                  type="button" 
                  className="password-toggle"
                  onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                >
                  <span>{showPasswords.confirm ? '🙈' : '👁'}</span>
                </button>
              </div>
              {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#ef4444', 
                  marginTop: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>❌</span>
                  <span>Passwords do not match</span>
                </div>
              )}
              {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#10b981', 
                  marginTop: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>✅</span>
                  <span>Passwords match</span>
                </div>
              )}
            </div>
            
            <button 
              type="submit" 
              className="login-submit-btn" 
              disabled={loading || formData.resetCode.length !== 6 || formData.newPassword !== formData.confirmPassword || formData.newPassword.length < 8}
              style={{
                opacity: (loading || formData.resetCode.length !== 6 || formData.newPassword !== formData.confirmPassword || formData.newPassword.length < 8) ? 0.6 : 1
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>🔄</span>
                  <span>Resetting Password...</span>
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>🔐</span>
                  <span>Reset Password</span>
                </span>
              )}
            </button>

            <div className="forgot-password" style={{ textAlign: 'center', marginTop: '20px' }}>
              <Link to="/login" className="forgot-password-link">
                ← Back to Login
              </Link>
              <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
              <Link to="/forgot-password" className="forgot-password-link">
                Resend Code
              </Link>
            </div>
          </form>

          <div style={{ 
            textAlign: 'center', 
            marginTop: '20px', 
            padding: '16px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
              🛡️ <strong>Security Notice:</strong> After resetting your password, you'll be redirected to the login page. 
              Please use your new password to sign in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;